import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  makeStyles,
  IconButton,
} from "@material-ui/core";
import { ExpandMore as ExpandMoreIcon, Close as CloseIcon } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    background: "linear-gradient(to right, #182229, #202d37)",
    color: "#ffffff",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
  },
  dialogTitleText: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.15rem",
  },
  dialogContent: {
    padding: "16px 24px",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  },
  dialogActions: {
    padding: "12px 24px",
    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: theme.palette.background.default,
  },
  closeBtn: {
    color: "#ffffff",
    padding: 4,
  },
  accordion: {
    marginBottom: 8,
    borderRadius: 8,
    overflow: "hidden",
    "&::before": { display: "none" },
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  accordionSummary: {
    backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.05)",
    minHeight: 56,
    "&$expanded": { minHeight: 56 },
  },
  expanded: {},
  accIcon: {
    color: theme.palette.primary.main,
    fontSize: 22,
    marginRight: 10,
  },
  accTitle: {
    fontWeight: 600,
    fontSize: "0.98rem",
    display: "flex",
    alignItems: "center",
  },
  accDetails: {
    padding: "12px 20px 16px",
    display: "block",
  },
  paragraph: {
    marginBottom: 10,
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  introBox: {
    backgroundColor: theme.mode === "light" ? "rgba(0, 122, 255, 0.08)" : "rgba(0, 122, 255, 0.12)",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: 6,
    padding: "12px 16px",
    marginBottom: 16,
  },
  list: {
    paddingLeft: "22px",
    marginBottom: 12,
    "& li": {
      fontSize: "0.9rem",
      lineHeight: 1.55,
      marginBottom: 6,
    },
  },
  ruleBox: {
    backgroundColor: theme.mode === "light" ? "rgba(255, 193, 7, 0.12)" : "rgba(255, 193, 7, 0.08)",
    borderLeft: "4px solid #ffc107",
    borderRadius: 6,
    padding: "10px 14px",
    marginTop: 8,
    marginBottom: 8,
    "& .ruleTitle": {
      fontWeight: 700,
      fontSize: "0.85rem",
      color: "#b8860b",
      marginBottom: 4,
    },
    "& p": {
      fontSize: "0.85rem",
      margin: 0,
      lineHeight: 1.5,
    },
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: theme.palette.primary.main,
    marginTop: 10,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
}));

const Regra = ({ titulo, children }) => {
  const classes = useStyles();
  return (
    <Box className={classes.ruleBox}>
      <div className="ruleTitle">⚠️ Atenção — {titulo}</div>
      <p>{children}</p>
    </Box>
  );
};

const TutorialTemplates = ({ open, onClose }) => {
  const classes = useStyles();
  const [expandido, setExpandido] = useState("visao-geral");

  const handleChange = (panel) => (event, isExpanded) => {
    setExpandido(isExpanded ? panel : false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="tutorial-templates-title"
    >
      <DialogTitle id="tutorial-templates-title" className={classes.dialogTitle} disableTypography>
        <Typography component="h2" className={classes.dialogTitleText}>
          🎓 Tutorial Detalhado — Tela de Templates
        </Typography>
        <IconButton size="small" className={classes.closeBtn} onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        <Box className={classes.introBox}>
          <Typography className={classes.paragraph} style={{ marginBottom: 0 }}>
            Este tutorial explica <strong>detalhadamente</strong> como usar a tela de Templates e as
            regras de cada funcionalidade. Clique em cada seção abaixo para abrir e ver as explicações.
          </Typography>
        </Box>

        {/* 1. VISÃO GERAL */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "visao-geral"}
          onChange={handleChange("visao-geral")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🏠</span> Visão Geral da Tela
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Esta tela serve para gerenciar os <strong>modelos de mensagens aprovados pelo
              WhatsApp</strong> (a Meta, empresa dona do WhatsApp). Esses modelos são necessários
              quando você quer mandar uma mensagem para um cliente que <strong>ainda não mandou
              mensagem para você</strong>, ou quando já se passaram mais de 24 horas desde a última
              vez que ele falou com a empresa.
            </Typography>
            <Typography className={classes.paragraph}>
              Em outras palavras: fora da janela de 24 horas, o WhatsApp <strong>só deixa enviar
              mensagens usando um modelo aprovado</strong>. Você não pode simplesmente digitar e
              enviar qualquer texto livre — precisa ser um template.
            </Typography>
            <Typography className={classes.paragraph}>
              A tela é dividida em três partes:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Topo:</strong> título da tela, botão "Atualizar" (para recarregar a lista) e botão "Criar Template" (para criar um modelo novo).</li>
              <li><strong>Barra de filtros:</strong> seletor da conexão WhatsApp, campo de busca e contador de quantos templates foram encontrados.</li>
              <li><strong>Tabela:</strong> a lista com todos os modelos, mostrando nome, categoria, idioma, status e botões de editar/deletar.</li>
            </ul>

            <Regra titulo="Só aparece para quem tem permissão">
              O menu de Templates só aparece para usuários que têm a permissão "ver Templates"
              liberada no cadastro. Se você não vê no menu, peça para o administrador liberar.
            </Regra>

            <Regra titulo="Só funciona com API Oficial">
              Os templates só existem em números de WhatsApp conectados pela <strong>API Oficial da
              Meta</strong>. Números comuns (conectados por QR code, por exemplo) não têm templates.
              Por isso, o seletor de conexão só mostra as conexões oficiais.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 2. SELETOR DE CONEXÃO */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "conexao"}
          onChange={handleChange("conexao")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>📱</span> Seletor de Conexão WhatsApp
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              A primeira coisa que você vê na tela é a caixa <strong>"Conexão WhatsApp"</strong>.
              Como os templates pertencem ao número de telefone (e não ao sistema em geral), você
              precisa escolher de qual número quer ver os modelos.
            </Typography>
            <ul className={classes.list}>
              <li>Ao entrar na tela, o sistema <strong>já escolhe automaticamente</strong> o primeiro número oficial disponível.</li>
              <li>Se você trocar de conexão, a lista recarrega mostrando os templates daquele número.</li>
              <li>Se não tiver nenhuma conexão oficial conectada, a lista fica vazia e aparece a mensagem "Selecione uma conexão WhatsApp para ver os templates".</li>
            </ul>

            <Regra titulo="Só conexões oficiais aparecem">
              No seletor só aparecem conexões da <strong>API Oficial</strong> que estejam conectadas
              ou em processo de conexão. Conexões comuns (por QR code) não aparecem, porque não têm
              templates.
            </Regra>

            <Regra titulo="Cada número tem seus próprios templates">
              Os templates são criados e aprovados por número. Um modelo criado no número "Vendas"
              não aparece no número "Suporte". Cada um tem a sua própria lista.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 3. BUSCA E ATUALIZAR */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "busca-atualizar"}
          onChange={handleChange("busca-atualizar")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🔄</span> Busca e Botão Atualizar
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <div className={classes.sectionLabel}>Campo de Busca</div>
            <ul className={classes.list}>
              <li><strong>"Pesquisar template...":</strong> digite parte do nome do modelo e a lista filtra na hora, enquanto você digita.</li>
              <li>A busca é pelo <strong>nome</strong> do template (ex: digite "boas" para achar "boas_vindas").</li>
              <li>Do lado da busca aparece um contador: <strong>"X templates encontrados"</strong>, mostrando quantos modelos apareceram depois da filtragem.</li>
            </ul>

            <div className={classes.sectionLabel}>Botão Atualizar</div>
            <ul className={classes.list}>
              <li>O botão <strong>"Atualizar"</strong> (com ícone de seta girando) recarrega a lista de templates direto da Meta.</li>
              <li>É importante usar depois de criar um template novo, para ver se o status já mudou de "Pendente" para "Aprovado".</li>
              <li>Fica desativado se você não escolheu nenhuma conexão.</li>
            </ul>

            <Regra titulo="A aprovação demora">
              Depois de criar um template, o WhatsApp analisa e aprova (ou reprova). Isso pode levar
              de alguns minutos a algumas horas. Se o status ainda estiver "Pendente", clique em
              Atualizar depois de um tempo para ver se mudou.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 4. TABELA E STATUS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "tabela-status"}
          onChange={handleChange("tabela-status")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>📋</span> Tabela, Categorias e Status
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              A tabela mostra todos os modelos da conexão escolhida, com cinco colunas:
            </Typography>

            <div className={classes.sectionLabel}>Colunas da tabela</div>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> o nome do modelo (ex: <code>boas_vindas_cliente</code>).</li>
              <li><strong>Categoria:</strong> um quadradinho colorido com o tipo da mensagem (explicado abaixo).</li>
              <li><strong>Idioma:</strong> o idioma do modelo (ex: <code>pt_BR</code> = português do Brasil, <code>en_US</code> = inglês, <code>es_ES</code> = espanhol).</li>
              <li><strong>Status:</strong> um quadradinho colorido dizendo se o WhatsApp já aprovou ou não (explicado abaixo).</li>
              <li><strong>Ações:</strong> botões de editar (lápis) e deletar (lixeira).</li>
            </ul>

            <div className={classes.sectionLabel}>Categorias (tipo da mensagem)</div>
            <ul className={classes.list}>
              <li><strong>Marketing (azul):</strong> promoções, ofertas, cupons, lembretes comerciais. Tudo que for divulgar algo para o cliente.</li>
              <li><strong>Utilidade (cinza):</strong> avisos importantes e transacionais — comprovantes, alertas de envio, faturas, confirmações de agendamento.</li>
              <li><strong>Autenticação (vermelho):</strong> códigos de segurança temporários, como PIN de login em duas etapas.</li>
            </ul>

            <div className={classes.sectionLabel}>Status (situação da aprovação)</div>
            <ul className={classes.list}>
              <li><strong>Aprovado (azul):</strong> o WhatsApp aceitou. Pronto para usar em disparos e atendimentos.</li>
              <li><strong>Pendente (cinza):</strong> enviado para o WhatsApp, aguardando a análise deles.</li>
              <li><strong>Rejeitado (vermelho):</strong> o WhatsApp não aceitou. Não pode ser usado. Se passar o mouse em cima, aparece o <strong>motivo da rejeição</strong>.</li>
              <li><strong>Desabilitado (cinza):</strong> o modelo existe mas está desligado e não pode ser usado no momento.</li>
            </ul>

            <Regra titulo="Motivo da rejeição">
              Quando um template é rejeitado, você pode passar o mouse em cima do status (o cursor
              vira uma setinha com ponto de interrogação) e ler o motivo. Assim você sabe o que
              corrigir e pode criar um novo modelo sem o erro.
            </Regra>

            <Regra titulo="Template rejeitado não dá para editar">
              Se um template foi rejeitado, você geralmente precisa <strong>criar um novo</strong>{" "}
              corrigindo o problema. O WhatsApp não permite reaprovar um modelo já rejeitado — é
              preciso excluir e criar do zero.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 5. EDITAR E DELETAR */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "editar-deletar"}
          onChange={handleChange("editar-deletar")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>⚙️</span> Botões Editar e Deletar
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Cada linha da tabela tem dois botões no final:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Editar (lápis):</strong> abre a tela do modelo para você alterar algumas coisas. O que pode ser mudado depende do que o WhatsApp permite — geralmente dá para mudar a categoria e o texto do corpo.</li>
              <li><strong>Deletar (lixeira):</strong> abre uma janelinha de confirmação perguntando "Tem certeza?". Se confirmar, o modelo é excluído do sistema e do WhatsApp. <strong>Não dá para desfazer.</strong></li>
            </ul>

            <Regra titulo="Nome e idioma não mudam depois de criados">
              Ao editar, o <strong>nome</strong> e o <strong>idioma</strong> do template ficam
              bloqueados (não dá para clicar). Isso é uma regra do WhatsApp: uma vez criado, o nome
              e o idioma são definitivos. Se precisar mudar, é preciso excluir e criar um novo.
            </Regra>

            <Regra titulo="Exclusão é definitiva">
              Ao deletar um template, ele sai do sistema e é solicitada a exclusão no WhatsApp. Não
              tem como recuperar. Se precisar do modelo de novo, terá que criá-lo do zero e esperar
              a aprovação de novo.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 6. CRIAR TEMPLATE */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "criar-template"}
          onChange={handleChange("criar-template")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>➕</span> Criar um Novo Template
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Ao clicar em <strong>"Criar Template"</strong>, abre uma janela grande com o
              formulário. Ele tem várias partes:
            </Typography>

            <div className={classes.sectionLabel}>Dados principais (topo do formulário)</div>
            <ul className={classes.list}>
              <li><strong>Nome do Template:</strong> só letras minúsculas, números e underline (_). Sem espaços, sem acentos, sem letras maiúsculas. Exemplo: <code>lembrete_vencimento</code>. O sistema já arruda sozinho enquanto você digita (transforma em minúscula e tira acentos).</li>
              <li><strong>Categoria:</strong> escolha entre Marketing, Utilidade ou Autenticação (explicado na seção da tabela).</li>
              <li><strong>Idioma:</strong> português do Brasil (pt_BR), inglês (en_US) ou espanhol (es_ES).</li>
              <li><strong>Formato dos Parâmetros:</strong> como as variáveis vão aparecer no texto. Pode ser <em>posicional</em> ({`{1}`}, {`{2}`}) ou <em>nomeado</em> ({`{nome}`}, {`{data}`}). O mais comum é posicional.</li>
            </ul>

            <Regra titulo="Nome é para sempre">
              O nome do template <strong>não pode ser alterado depois de criado</strong>. Pense bem
              no nome antes de salvar, porque se errar vai precisar excluir e criar do zero.
            </Regra>

            <Regra titulo="Regras do nome">
              O nome só aceita letras minúsculas (a-z), números e o sinal de underline (_). Nada de
              espaços, acentos, cedilha, letras maiúsculas ou caracteres especiais. O sistema limpa
              sozinho o que não for permitido enquanto você digita.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 7. COMPONENTES */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "componentes"}
          onChange={handleChange("componentes")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🧩</span> Componentes da Mensagem
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Um template é montado por <strong>partes</strong> chamadas componentes. Cada parte tem
              um tipo. Você pode ter vários componentes num mesmo template:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Cabeçalho (HEADER):</strong> o texto ou a imagem/vídeo/documento que aparece <em>em cima</em> da mensagem. É opcional. Se for imagem, vídeo ou documento, você faz o upload do arquivo.</li>
              <li><strong>Corpo (BODY):</strong> é o texto principal, o que o cliente vai ler. <strong>Obrigatório</strong> — todo template precisa ter pelo menos um corpo.</li>
              <li><strong>Rodapé (FOOTER):</strong> um texto menor que aparece <em>embaixo</em>. É opcional. Exemplo: "Responda STOP para parar".</li>
              <li><strong>Botões (BUTTONS):</strong> botões interativos que o cliente pode tocar. É opcional.</li>
            </ul>

            <div className={classes.sectionLabel}>Variáveis (espaços a preencher depois)</div>
            <Typography className={classes.paragraph}>
              No texto do corpo e do cabeçalho, você pode usar <strong>variáveis</strong>, que são
              espaços que serão preenchidos na hora de enviar. Elas aparecem entre chaves:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Posicionais:</strong> {`{1}`}, {`{2}`}, {`{3}`}... Na hora do disparo, você informa o valor de cada uma na ordem.</li>
              <li><strong>Nomeadas:</strong> {`{nome}`}, {`{data}`}, {`{hora}`}... Mais fáceis de entender, mas precisam ter exemplos cadastrados.</li>
              <li><strong>Atalhos prontos:</strong> o sistema oferece algumas variáveis especiais que se preenchem sozinhas: <strong>{`{{name}}`}</strong> (nome do contato), <strong>{`{{firstName}}`}</strong> (primeiro nome), <strong>{`{{ms}}`}</strong> (saudação), <strong>{`{{protocol}}`}</strong> (protocolo), <strong>{`{{hour}}`}</strong> (hora), <strong>{`{{date}}`}</strong> (data). É só clicar nelas para inserir.</li>
            </ul>

            <Regra titulo="Precisa ter pelo menos um corpo">
              Todo template <strong>obrigatoriamente</strong> precisa ter um componente do tipo
              "Corpo" (BODY). Se você não tiver, o sistema não deixa salvar.
            </Regra>

            <Regra titulo="Cabeçalho: texto OU mídia, nunca os dois">
              No cabeçalho você pode ter <strong>ou</strong> um texto <strong>ou</strong> uma
              imagem/vídeo/documento, mas não os dois ao mesmo tempo. Escolha um tipo e siga com ele.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 8. UPLOAD DE MÍDIA */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "upload-midia"}
          onChange={handleChange("upload-midia")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>📎</span> Upload de Imagem, Vídeo ou Documento no Cabeçalho
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Se você escolher que o cabeçalho vai ter mídia (em vez de texto), aparece uma caixinha
              tracejada onde você clica para enviar o arquivo. O sistema aceita:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Imagens:</strong> JPG, JPEG, PNG, GIF e WEBP.</li>
              <li><strong>Vídeo:</strong> MP4.</li>
              <li><strong>Documento:</strong> PDF.</li>
            </ul>

            <div className={classes.sectionLabel}>Como funciona</div>
            <ul className={classes.list}>
              <li>Clique na caixinha tracejada e escolha o arquivo no computador.</li>
              <li>Aparece um círculo girando enquanto o arquivo sobe.</li>
              <li>Quando termina, aparece a prévia (a imagem, o vídeo ou o documento) e um botão <strong>"Remover Mídia"</strong> se quiser trocar.</li>
            </ul>

            <Regra titulo="Tamanho máximo de 5 MB">
              O arquivo não pode passar de <strong>5 megabytes</strong>. Se for maior, o sistema
              avisa e não deixa enviar.
            </Regra>

            <Regra titulo="Só os formatos aceitos">
              Só dá para enviar JPG, JPEG, PNG, GIF, WEBP, MP4 e PDF. Outros formatos (como Word,
              Excel, etc.) não são aceitos no cabeçalho do template.
            </Regra>

            <Regra titulo="A imagem precisa de um link público">
              Para o WhatsApp aceitar a mídia, ela precisa estar em um link público com HTTPS (site
              seguro). O sistema cuida disso para você automaticamente quando faz o upload.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 9. BOTÕES INTERATIVOS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "botoes"}
          onChange={handleChange("botoes")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🔘</span> Botões Interativos
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Você pode adicionar botões no template para o cliente tocar e interagir. Existem
              <strong> botões de adicionar rápido</strong> logo no início da seção de componentes,
              e também dá para adicionar dentro de um componente do tipo "Botões".
            </Typography>

            <div className={classes.sectionLabel}>Atalhos de adicionar rápido</div>
            <ul className={classes.list}>
              <li><strong>Website (ícone de link):</strong> adiciona um botão que abre um site.</li>
              <li><strong>Telefone (ícone de telefone):</strong> adiciona um botão para o cliente ligar.</li>
              <li><strong>Copiar Código (ícone de código):</strong> adiciona um botão que copia um código (ex: número do pedido).</li>
              <li><strong>Resposta Rápida (ícone de mensagem):</strong> adiciona um botão de resposta rápida, que o cliente toca e manda uma resposta pronta.</li>
            </ul>

            <div className={classes.sectionLabel}>Tipos de botão</div>
            <ul className={classes.list}>
              <li><strong>Resposta Rápida (QUICK_REPLY):</strong> o cliente toca e o sistema entende como uma resposta pronta. Só precisa do texto do botão.</li>
              <li><strong>Website (URL):</strong> abre um site. Precisa do texto do botão e do endereço do site (ex: <code>https://www.exemplo.com</code>).</li>
              <li><strong>Telefone (PHONE_NUMBER):</strong> liga para um número. Precisa do texto do botão e do número com código do país (ex: <code>+5511999999999</code>).</li>
              <li><strong>Copiar Código (COPY_CODE):</strong> copia um código para a área de transferência do cliente. Precisa de um exemplo de código.</li>
            </ul>

            <Regra titulo="Limite de botões">
              O WhatsApp tem regras de quantos botões você pode colocar. Para mensagens de
              Marketing, o limite é mais restrito (até 3). Para outros tipos pode ser um pouco mais.
              Não exagere — foque nos botões mais importantes.
            </Regra>

            <Regra titulo="Botão Parar promoções (Opt-out)">
              Quando a categoria é <strong>Marketing</strong>, aparece um botão extra chamado{" "}
              <strong>"Botão Opt-out"</strong>. Ele adiciona automaticamente um botão de resposta
              rápida com o texto "Parar promoções". Isso serve para o cliente pedir para não receber
              mais mensagens promocionais — uma exigência do WhatsApp para campanhas de marketing.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 10. JANELA 24H */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "janela-24h"}
          onChange={handleChange("janela-24h")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>⏰</span> A Regra das 24 Horas
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              O WhatsApp tem uma regra importante chamada <strong>janela de 24 horas</strong>:
            </Typography>
            <ul className={classes.list}>
              <li>Quando o cliente manda uma mensagem para a empresa, abre uma janela de 24 horas onde você pode mandar mensagens livres (qualquer texto) de volta.</li>
              <li>Depois de 24 horas sem o cliente responder, essa janela <strong>fecha</strong>. A partir daí, você <strong>só pode mandar mensagens usando um template aprovado</strong>.</li>
              <li>É por isso que os templates existem: para permitir que a empresa reinicie o contato com o cliente mesmo depois da janela fechada.</li>
            </ul>

            <Regra titulo="Template só para iniciar conversa">
              Os templates servem justamente para esses momentos em que a janela de 24 horas está
              fechada. Depois que o cliente responde ao template, a janela abre de novo e você volta
              a poder mandar mensagens livres normalmente.
            </Regra>

            <Regra titulo="Mensagens livres fora da janela são bloqueadas">
              Se você tentar mandar uma mensagem comum (sem ser template) para um cliente fora da
              janela de 24 horas, o <strong>WhatsApp bloqueia o envio</strong>. Não adianta insistir
              — é regra deles. Tem que ser por template.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 11. EXEMPLOS PRÁTICOS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "exemplos"}
          onChange={handleChange("exemplos")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>💡</span> Exemplos Práticos do Dia a Dia
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <div className={classes.sectionLabel}>Exemplo 1: Lembrete de cobrança (Utilidade)</div>
            <Typography className={classes.paragraph}>
              O setor financeiro quer avisar os clientes que a fatura venceu. O atendente entra na
              tela de Templates, clica em <strong>Criar Template</strong> e preenche:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> <code>lembrete_vencimento</code></li>
              <li><strong>Categoria:</strong> Utilidade</li>
              <li><strong>Idioma:</strong> pt_BR</li>
              <li><strong>Corpo:</strong> "Olá, {`{1}`}! Sua fatura vence em {`{2}`} no valor de R$ {`{3}`}. Acesse o portal para pagar."</li>
              <li><strong>Botão:</strong> Website, com link para o portal do cliente.</li>
            </ul>
            <Typography className={classes.paragraph}>
              Depois de aprovado, o setor de cobrança pode disparar esse modelo para os clientes,
              preenchendo as variáveis (1 = nome, 2 = data, 3 = valor).
            </Typography>

            <div className={classes.sectionLabel}>Exemplo 2: Promoção com imagem (Marketing)</div>
            <Typography className={classes.paragraph}>
              O marketing quer mandar uma promoção com banner. Clica em <strong>Criar Template</strong>:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> <code>promo_dia_maes</code></li>
              <li><strong>Categoria:</strong> Marketing</li>
              <li><strong>Cabeçalho:</strong> faz o upload da imagem do banner.</li>
              <li><strong>Corpo:</strong> "Olá, {`{1}`}! Preparamos um cupom de 20% para o Dia das Mães. Aproveite!"</li>
              <li><strong>Botões:</strong> dois de Resposta Rápida: "Quero o cupom" e "Não tenho interesse".</li>
              <li><strong>Botão Opt-out:</strong> clica para adicionar o "Parar promoções" (exigência do WhatsApp para marketing).</li>
            </ul>
            <Typography className={classes.paragraph}>
              Depois de aprovado, é só usar em uma campanha para todos os clientes.
            </Typography>

            <div className={classes.sectionLabel}>Exemplo 3: Código de segurança (Autenticação)</div>
            <Typography className={classes.paragraph}>
              O sistema precisa mandar um código de login. Cria um template:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> <code>codigo_acesso</code></li>
              <li><strong>Categoria:</strong> Autenticação</li>
              <li><strong>Corpo:</strong> "Seu código de acesso é {`{1}`}. Não compartilhe com ninguém."</li>
              <li><strong>Botão:</strong> Copiar Código, para o cliente tocar e copiar o código direto.</li>
            </ul>
          </AccordionDetails>
        </Accordion>

        {/* 12. REGRAS GLOBAIS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "regras-globais"}
          onChange={handleChange("regras-globais")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🛡️</span> Regras Gerais dos Templates
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Regra titulo="Aprovação pelo WhatsApp">
              Todo template precisa ser <strong>aprovado pelo WhatsApp</strong> antes de poder ser
              usado. O tempo de análise varia: pode ser alguns minutos ou algumas horas. O sistema
              não tem como acelerar isso — é o WhatsApp que decide.
            </Regra>
            <Regra titulo="Nome não muda">
              O nome do template é <strong>definitivo</strong>. Depois de criado, não dá para
              alterar. Se errou o nome, exclua e crie de novo.
            </Regra>
            <Regra titulo="Idioma não muda">
              O idioma também <strong>não pode ser alterado</strong> depois de criado. Se precisar
              do mesmo modelo em outro idioma, crie um novo template naquele idioma.
            </Regra>
            <Regra titulo="Variáveis na ordem certa">
              Ao usar variáveis posicionais ({`{1}`}, {`{2}`}), elas precisam estar em ordem
              sequencial. Na hora de disparar, o sistema vai pedir o valor de cada uma para montar a
              mensagem completa para o cliente.
            </Regra>
            <Regra titulo="Templates por número">
              Cada número de WhatsApp oficial tem os seus próprios templates. Um modelo criado num
              número não aparece nem funciona em outro. Se você tem vários números, precisa criar os
              templates em cada um deles.
            </Regra>
            <Regra titulo="Só API Oficial">
              Templates só funcionam em números conectados pela <strong>API Oficial do WhatsApp</strong>.
              Números conectados por QR code (comuns) não têm acesso a templates.
            </Regra>
            <Regra titulo="Permissão de acesso">
              Só conseguem ver e usar esta tela os usuários com a permissão "ver Templates"
              liberada no cadastro. O administrador controla quem pode acessar.
            </Regra>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}
        >
          Entendi, fechar tutorial
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialTemplates;
